import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export type WalletLedgerEntry = {
  id: string;
  wallet_id: string;
  occurred_at: string;
  direction: "credit" | "debit";
  amount_ugx: number;
  kind: string;
  title: string;
  subtitle: string | null;
  ref: string | null;
  counterparty_wallet_id: string | null;
  student_id: string | null;
  source_type: string | null;
  running_balance_ugx: number;
};

export type WalletStatement = {
  wallet_id: string;
  cached_balance_ugx: number;
  computed_balance_ugx: number;
  reconciled: boolean;
  total_credits_ugx: number;
  total_debits_ugx: number;
  entries: WalletLedgerEntry[];
};

/**
 * Read the full wallet statement for the signed-in guardian's parent wallet
 * (or a specified wallet the caller is authorised to see). Server computes
 * running balances so the client never re-derives money math.
 */
export const getWalletStatement = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { walletId?: string; limit?: number; search?: string; direction?: "all" | "credit" | "debit"; studentId?: string | null }) => d)
  .handler(async ({ data, context }): Promise<WalletStatement> => {
    const { supabase, userId } = context;

    let walletId = data.walletId;
    if (!walletId) {
      const { data: w } = await supabase.from("wallets").select("id").eq("owner_id", userId).eq("owner_type", "parent").maybeSingle();
      if (!w) throw new Error("No wallet");
      walletId = w.id;
    }

    let q = supabase.from("wallet_ledger_entries_v2")
      .select("id,wallet_id,occurred_at,direction,amount_ugx,kind,title,subtitle,ref,counterparty_wallet_id,student_id,source_type")
      .eq("wallet_id", walletId)
      .order("occurred_at", { ascending: false })
      .limit(Math.min(data.limit ?? 500, 2000));

    if (data.direction && data.direction !== "all") q = q.eq("direction", data.direction);
    if (data.studentId) q = q.eq("student_id", data.studentId);
    if (data.search && data.search.trim()) {
      const s = data.search.trim().replace(/[%,]/g, "");
      q = q.or(`title.ilike.%${s}%,subtitle.ilike.%${s}%,ref.ilike.%${s}%`);
    }

    const { data: rows, error } = await q;
    if (error) throw error;

    const { data: wal } = await supabase.from("wallets").select("balance_ugx").eq("id", walletId).maybeSingle();
    const cached = wal?.balance_ugx ?? 0;

    // Running balance is computed on the full ledger sum then rolled forward chronologically
    // over just the visible slice, so filter views still show a truthful trail.
    const asc = [...(rows ?? [])].reverse();
    let credits = 0, debits = 0;
    for (const r of asc) {
      if (r.direction === "credit") credits += r.amount_ugx;
      else debits += r.amount_ugx;
    }
    const computed = credits - debits;

    // Build running balance walking backwards from cached balance across visible rows only.
    let bal = cached;
    const withBalance: WalletLedgerEntry[] = (rows ?? []).map((r) => {
      const row: WalletLedgerEntry = { ...(r as any), running_balance_ugx: bal };
      bal = bal - (r.direction === "credit" ? r.amount_ugx : -r.amount_ugx);
      return row;
    });

    return {
      wallet_id: walletId,
      cached_balance_ugx: cached,
      computed_balance_ugx: computed,
      reconciled: cached === computed || rows?.length === (data.limit ?? 500),
      total_credits_ugx: credits,
      total_debits_ugx: debits,
      entries: withBalance,
    };
  });

/**
 * Atomic transfer between two wallets via the SECURITY DEFINER helper.
 * Guarantees double-entry: the debit and credit both land or neither does.
 */
export const postWalletTransfer = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: {
    fromWalletId?: string | null;
    toWalletId?: string | null;
    amount: number;
    kind: string;
    titleOut: string;
    titleIn: string;
    subtitle?: string;
    ref?: string;
    studentId?: string | null;
    sourceType?: string;
    sourceId?: string;
  }) => d)
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    if (!data.amount || data.amount <= 0) throw new Error("Amount must be positive");
    if (!data.fromWalletId && !data.toWalletId) throw new Error("Provide at least one wallet");

    // Authorisation: caller must own or be linked to any wallet they debit.
    if (data.fromWalletId) {
      const { data: own } = await supabase.from("wallets").select("owner_id").eq("id", data.fromWalletId).maybeSingle();
      if (!own) throw new Error("Source wallet not found");
      if (own.owner_id !== userId) {
        const { data: link } = await supabase.from("wallet_links").select("id").eq("child_wallet_id", data.fromWalletId).eq("guardian_id", userId).maybeSingle();
        if (!link) throw new Error("Not allowed to move funds from this wallet");
      }
    }

    const { data: ref, error } = await (supabase.rpc as any)("post_wallet_transfer_v2", {
      _from_wallet: data.fromWalletId ?? undefined,
      _to_wallet: data.toWalletId ?? undefined,
      _amount: data.amount,
      _kind: data.kind,
      _title_out: data.titleOut,
      _title_in: data.titleIn,
      _subtitle: data.subtitle ?? undefined,
      _ref: data.ref ?? undefined,
      _guardian_id: userId,
      _student_id: data.studentId ?? undefined,
      _source_type: data.sourceType ?? undefined,
      _source_id: data.sourceId ?? undefined,
    });
    if (error) throw new Error(error.message);
    return { ref: ref as string };
  });
